import { useEffect, useRef, RefObject } from 'react';

/**
 * Message types for iframe-parent communication
 */
export type EmbedMessageType =
  | 'FORM_RESIZE'
  | 'FORM_SUBMIT_SUCCESS'
  | 'FORM_ERROR'
  | 'FLOW_RESIZE'
  | 'FLOW_COMPLETE'
  | 'FLOW_PROGRESS_UPDATE'
  | 'FLOW_ERROR';

/**
 * Data structure for embed messages
 */
export interface EmbedMessage {
  type: EmbedMessageType;
  formId?: string | number;
  [key: string]: any;
}

/**
 * Send a message to the parent window (if embedded in iframe)
 *
 * @param type - Type of message to send
 * @param data - Additional data to include in the message
 */
export const sendEmbedMessage = (
  type: EmbedMessageType,
  data: Record<string, any> = {}
): void => {
  // Only send message if we're in an iframe
  if (typeof window !== 'undefined' && window.parent !== window) {
    const message: EmbedMessage = {
      type,
      ...data,
    };

    // Determine target origin for security
    const rootElement = document.getElementById('form-embed-root');
    const allowedOrigin = rootElement?.getAttribute('data-allowed-origin') || '*';

    // Send to parent with a specific origin if provided, otherwise wildcard
    window.parent.postMessage(message, allowedOrigin);
  }
};

/**
 * Debounce helper function
 */
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Custom hook to observe element height changes and notify parent via postMessage
 *
 * @param ref - React ref to the element to observe
 * @param onResize - Callback when resize occurs
 * @param formId - Form ID to include in messages
 * @param debounceMs - Debounce delay in milliseconds (default: 300ms)
 */
export const useResizeObserver = (
  ref: RefObject<HTMLElement | null>,
  onResize: (height: number) => void,
  formId?: string | number,
  debounceMs: number = 300
): void => {
  const observerRef = useRef<ResizeObserver | null>(null);

  const lastHeightRef = useRef<number>(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Create debounced resize handler
    const debouncedResize = debounce((height: number) => {
      // Only update if height changed by more than 10px to prevent infinite loops
      // from tiny layout shifts
      const heightDifference = Math.abs(height - lastHeightRef.current);
      if (heightDifference > 10) {
        lastHeightRef.current = height;
        onResize(height);

        // Send message to parent if we have a formId
        if (formId) {
          sendEmbedMessage('FORM_RESIZE', {
            formId,
            height,
          });
        }
      }
    }, debounceMs);

    // Get accurate height measurement
    const getHeight = () => {
      // Use element's scrollHeight directly to avoid feedback loops
      // The element ref should be the container that holds all form content
      return element.scrollHeight;
    };

    const updateHeight = () => {
      const height = getHeight();
      debouncedResize(height);
    };

    // Only send initial height on first mount, not on every effect run
    if (lastHeightRef.current === 0) {
      updateHeight();
    }

    // Create ResizeObserver
    observerRef.current = new ResizeObserver(updateHeight);

    // Also listen for window load to recalculate after all resources are loaded
    window.addEventListener('load', updateHeight);

    // Start observing only the element (not document.body to avoid feedback loops)
    observerRef.current.observe(element);

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      window.removeEventListener('load', updateHeight);
    };
  }, [ref, onResize, formId, debounceMs]);
};

/**
 * Hook to detect if the component is running in an iframe
 *
 * @returns true if running in an iframe, false otherwise
 */
export const useIsEmbedded = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.parent !== window;
};

/**
 * Send form submission success message to parent
 *
 * @param formId - Form ID
 * @param redirectUrl - Optional redirect URL
 */
export const sendFormSubmitSuccess = (
  formId: string | number,
  redirectUrl?: string
): void => {
  sendEmbedMessage('FORM_SUBMIT_SUCCESS', {
    formId,
    ...(redirectUrl && { redirectUrl }),
  });
};

/**
 * Send form error message to parent
 *
 * @param formId - Form ID
 * @param error - Error message
 */
export const sendFormError = (
  formId: string | number,
  error: string
): void => {
  sendEmbedMessage('FORM_ERROR', {
    formId,
    error,
  });
};

/**
 * Send flow completion message to parent
 *
 * @param flowId - Flow ID
 * @param completedForms - Number of completed forms
 * @param totalForms - Total number of forms in the flow
 * @param redirectUrl - Optional redirect URL
 */
export const sendFlowComplete = (
  flowId: string | number,
  completedForms: number,
  totalForms: number,
  redirectUrl?: string
): void => {
  sendEmbedMessage('FLOW_COMPLETE', {
    flowId,
    completedForms,
    totalForms,
    ...(redirectUrl && { redirectUrl }),
  });
};

/**
 * Send flow progress update message to parent
 *
 * @param flowId - Flow ID
 * @param completedForms - Number of completed forms
 * @param totalForms - Total number of forms in the flow
 * @param sessionId - Optional session ID
 */
export const sendFlowProgressUpdate = (
  flowId: string | number,
  completedForms: number,
  totalForms: number,
  sessionId?: string
): void => {
  const progress = totalForms > 0 ? (completedForms / totalForms) * 100 : 0;

  sendEmbedMessage('FLOW_PROGRESS_UPDATE', {
    flowId,
    completedForms,
    totalForms,
    progress,
    ...(sessionId && { sessionId }),
  });
};

/**
 * Send flow error message to parent
 *
 * @param flowId - Flow ID
 * @param error - Error message
 */
export const sendFlowError = (
  flowId: string | number,
  error: string
): void => {
  sendEmbedMessage('FLOW_ERROR', {
    flowId,
    error,
  });
};
